//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { AuthEnums } from "models/enums";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { useCheckRelations } from "components/common";
import { useAdvisorPermissions } from "components/advisor";
import useSalaryPermissions from "components/salary/hooks/useSalaryPermissions";
//#endregion
import { CompanyService } from "services";

function useEmployeeHelpers() {
  const isSalaryAdmin = CompanyService.hasRelation(AuthEnums.PersonCompanyRelationType.SalaryAdmin, true);

  const { getIsAdmin, getIsAuditor } = useCheckRelations();
  const isAdmin = getIsAdmin();
  const isAuditor = getIsAuditor();

  const manageableEmployees = useSelector(
    (state: AppState) => state.auth.businessUnitManagerSettings.manageableEmployees
  );

  const manageableEmployeesLoaded = useSelector(
    (state: AppState) => state.auth.businessUnitManagerSettings.manageableEmployeesLoaded
  );

  const superkollSalaryType = useSelector((state: AppState) => state.auth.selectedCompany.superkollSalaryType);

  const loggedInUserIsBusinessUnitManagerWithSalaryRights = useSelector(
    (state: AppState) => state.auth.selectedCompany.loggedInUserIsBusinessUnitManagerWithSalaryRights
  );

  const canBusinessUnitManagerViewEmployee = (employeeId: number) =>
    loggedInUserIsBusinessUnitManagerWithSalaryRights &&
    (!manageableEmployeesLoaded || manageableEmployees.includes(employeeId));

  const { isWintSalarySettingsAvailable } = useSalaryPermissions();

  // A company planned for Wint Salary manages salary settings even before it has a salary type of its own.
  const hasSalaryProcessing =
    superkollSalaryType !== WintSuperkollAPIModel.SuperkollSalaryType.None || isWintSalarySettingsAvailable;

  const canSalaryAdminManageSalary = isSalaryAdmin && hasSalaryProcessing;

  const canView = (employeeId: number) =>
    !isAuditor && (isAdmin || canSalaryAdminManageSalary || canBusinessUnitManagerViewEmployee(employeeId));

  const canCreate =
    !isAuditor && (isAdmin || canSalaryAdminManageSalary || loggedInUserIsBusinessUnitManagerWithSalaryRights);

  const canManageSalarySettings =
    (isSalaryAdmin || loggedInUserIsBusinessUnitManagerWithSalaryRights) && hasSalaryProcessing;

  // The bulk editor sends no Wint salary settings, which Core refuses once the settings belong to Wint.Salary.
  const canBulkEditSalaries = canManageSalarySettings && !isWintSalarySettingsAvailable;

  const canManageEmployeeSalary = (employeeId: number) =>
    canSalaryAdminManageSalary || canBusinessUnitManagerViewEmployee(employeeId);

  const { isAdvisor } = useAdvisorPermissions();

  const canAddExtraSalary =
    !isAdvisor && !isAuditor && isSalaryAdmin && superkollSalaryType !== WintSuperkollAPIModel.SuperkollSalaryType.None;

  return {
    isAuditor,
    isAdmin,
    isSalaryAdmin,
    superkollSalaryType,
    canView,
    canCreate,
    canManageSalarySettings,
    canBulkEditSalaries,
    canManageEmployeeSalary,
    canSalaryAdminManageSalary,
    loggedInUserIsBusinessUnitManagerWithSalaryRights,
    canAddExtraSalary,
  };
}

export default useEmployeeHelpers;
